import { Avatar, AspectRatio, Box, Heading, Image, Text, VStack, HStack, Separator } from '@chakra-ui/react'

import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider'
import { NextLink } from '@src/components/sco/utilities/NextLink'
import { useHover } from '@uidotdev/usehooks'

type ArticleCardProps = {
  image?: string
  summary?: string
  url?: string
  title?: string
  tag?: string
  subject?: string
  subjectLogo?: string
  showSummary?: boolean
  firstName?: string
  lastName?: string
  avatar?: string
  authors?: boolean
  large?: boolean
  ratio?: any
  ratioLg?: any
}

export const ArticleCard = (props: ArticleCardProps) => {
  const { getBreakpointValue } = useBreakpointContext()
  const [ref, hovering] = useHover()

  // Calculate the ratio based on props.ratioLg or props.ratio with a default of 16/9
  const calculatedRatio = props.ratioLg || props.ratio || 16 / 9

  // Card title sizing based on grid size

  const titleSize = props.large
    ? getBreakpointValue(['lg', 'xl', '3xl', '3xl', '3xl'])
    : getBreakpointValue(['lg', 'xl', 'xl', 'md'])

  return (
    <Box width={'full'}>
      <NextLink href={props.url || '#'} prefetch={false}>
        <VStack ref={ref} gap={6} height={'full'} transition={'all 0.2s ease-in'} width={'full'}>
          {/* COVER IMAGE */}
          <Box position={'relative'} width={'full'}>
            <AspectRatio
              borderRadius={'xl'}
              boxShadow={'0px 5px 20px -10px rgba(0, 0, 0, 0.8)'}
              overflow={'hidden'}
              ratio={calculatedRatio}
              width={'full'}
            >
              <Box
                background={`url('${props.image}')`}
                backgroundPosition={'center center'}
                backgroundRepeat={'no-repeat'}
                backgroundSize={'cover'}
                borderRadius={'xl'}
                overflow={'hidden'}
                transform={hovering && 'scale(1.05)'}
                transition={'all 0.2s ease-in'}
                width={'full'}
                zIndex={1}
              />
            </AspectRatio>
          </Box>

          {/* BODY */}
          <VStack
            alignItems={'flex-start'}
            alignSelf={'stretch'}
            flex={'1 0 0'}
            gap={2}
            paddingRight={10}
            width={'full'}
          >
            <Heading fontWeight={700} lineHeight={'normal'} size={titleSize} transition={'all 0.2s ease-in'}>
              {props.title}
            </Heading>
            {props.authors && (
              <HStack>
                <Avatar.Root boxShadow={'md'} size={getBreakpointValue(['sm'])}>
                  <Avatar.Fallback name={`${props.firstName} ${props.lastName}`} />
                  <Avatar.Image src={props.avatar} />
                </Avatar.Root>
                <Text
                  color={'colorPalette.fg/75'}
                  fontSize={'sm'}
                  fontWeight={600}
                  transition={'all 0.2s ease-in'}
                >
                  {props.firstName} {props.lastName}
                </Text>
              </HStack>
            )}
            <HStack
              align={'center'}
              display={props.subject || props.subjectLogo ? 'flex' : 'none'}
              gap={3}
              width={'full'}
            >
              {props.subject && (
                <Text fontSize={'sm'} fontWeight={600} opacity={0.75}>
                  {props.subject}
                </Text>
              )}
              <Separator height={'1em'} orientation={'vertical'} />
              {props.subjectLogo && (
                <Image
                  filter={{ _light: 'invert(1)', _dark: 'invert(0)' }}
                  maxHeight={'1em'}
                  maxWidth={'5em'}
                  objectFit={'contain'}
                  opacity={0.75}
                  src={props.subjectLogo}
                />
              )}
            </HStack>

            {props.showSummary && (
              <Text fontSize={'md'} lineClamp={4} opacity={0.8}>
                {props.summary}
              </Text>
            )}
          </VStack>
        </VStack>
      </NextLink>
    </Box>
  )
}
